/*
 * Where the app is RUNNING, as opposed to which API it talks to
 * (`REACT_APP_SERVICES_ENV`) or how it was built.
 *
 * This module must stay dependency-free: it is imported by the logging gate, which
 * `storageUtils` also feeds, so any import here risks a cycle.
 */

const LOCAL_HOSTNAMES = new Set(['127.0.0.1', '::1', '[::1]', 'localhost']);

const LOCAL_ENV_NAME = 'local';

/**
 * Whether the page is served from a loopback address, whatever port or protocol it
 * uses. Observed at runtime rather than configured, so a build nobody set up for local
 * use — a headless agent serving a deployed profile, say — is still recognised as local.
 * Falls back to `false` when there is no `location` (codegen, unit tests in node).
 */
export const isLocallyServed = (): boolean => {
  const { hostname } = globalThis.location ?? {};

  return hostname !== undefined && (LOCAL_HOSTNAMES.has(hostname) || hostname.endsWith('.localhost'));
};

/**
 * Whether this is a Cypress run inside CI. Such runs serve the build from localhost
 * inside the runner, so every locally-served rule has to exempt them: they are not
 * somebody's laptop, and their sessions must stay attributable to the build's own
 * environment for CI Visibility linking.
 */
export const isCypressCI = (): boolean => process.env.REACT_APP_CYPRESS_CI === 'true';

/**
 * The environment name telemetry should report itself under. Locally served sessions
 * that have explicitly opted in report `local`, never the deployed environment whose
 * config they happen to have loaded.
 */
export const getTelemetryEnvName = (): string => {
  const configuredEnvName = process.env.REACT_APP_FEATURE_DATADOG_ENV_NAME || '';

  return !isCypressCI() && isLocallyServed() ? LOCAL_ENV_NAME : configuredEnvName;
};
